import React, {FC, useState, useEffect} from "react"
import {withRouter, RouteComponentProps} from "react-router-dom"

import FormattedMessage, {getMessage, useIntl} from "@irati/utils/lang"
import magicLoginService from "@irati/auth/services/magicLogin"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import {CreateConfirmIdentifierResponse, CreateMagicLoginResponse, MagicLogin} from "@irati/types"
import magicLoginCodes from "@irati/auth/errorCodes/Magic"
import tracking, {LoginType, LoginProduct} from "@irati-tracking/platform/platform"
import {UserAccountForCookie, getUserAccountCookie} from "@irati/utils/cookies/userAccountCookie"
import getTenantFromPath from "@irati/identity/helpers/getTenantFromPath"
import History from "@irati/utils/history"
import Flex from "@irati/ui/src/Flex"
import Link from "@irati/ui/src/Link"
import {Body} from "@irati/ui/src/Typography"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {TREND_RATE_HUBS_DISMISS, TREND_RATE_HUB_VISIBLE} from "@irati-web/admin/src/components/Reporting/TrendRates/QuickActions"

import LoginBackground from "../../components/LoginBackground"
import EnterEmail from "./EnterEmail"
import ConfirmCode from "./ConfirmCode"
import DomainSelector from "./DomainSelector"
import ConfirmPhoneCode from "./ConfirmPhoneCode"
import VerifyPhone from "./VerifyPhone"
import EnterPhone from "./EnterPhone"
import {StyledTabs} from "./styled"

export const VERIFY_PHONE_NO = "authVerifyPhoneNo"

export enum ScreenTabs {
  email = "enterEmail",
  phoneNo = "enterPhoneno"
}

enum Screens {
  enterEmail = "enterEmail",
  confirmCode = "confirmCode",
  domainSelector = "selectDomain",
  verifyPhone = "verifyPhone",
  confirmPhoneCode = "confirmPhoneCode"
}

interface Props extends RouteComponentProps {
  confirmPhone?: boolean
}

const OneTimePassword: FC<Props> = ({location, confirmPhone}) => {
  const [screen, setScreen] = useState(confirmPhone ? Screens.verifyPhone : Screens.enterEmail)
  const [identifier, setIdentifier] = useState("")
  const [magicLogin, setMagicLogin] = useState<CreateMagicLoginResponse>()
  const [confirmPhoneLogin, setConfirmPhoneLogin] = useState<CreateConfirmIdentifierResponse>()
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [multipleDomains, setMultipleDomains] = useState<MagicLogin[]>()
  const [tenantSSO, setTenantSSO] = useState<MagicLogin>()
  const [loginWithPhone, setLoginWithPhone] = useState(false)
  const [activeKey, setActiveKey] = useState<ScreenTabs>(ScreenTabs.email)
  const [enabledSSO, setEnabledSSO] = useState(false)

  const {formatMessage} = useIntl()

  const handleLogin = (value: string, isPhone?: boolean) => {
    setLoginWithPhone(!!isPhone)
    setErrorMsg("")
    if (value) {
      setIdentifier(value)
      sendOTPCode(value)
    }
  }

  const handleVerifyPhoneNo = (phoneNo: string) => {
    setErrorMsg("")
    if (phoneNo) {
      setIdentifier(phoneNo)
      sendPhoneOTPCode(phoneNo)
    }
  }

  const sendPhoneOTPCode = (phoneNo: string) => {
    const loaded = magicLoginService
      .createConfirmIdentifier(phoneNo)
      .then((res) => {
        setConfirmPhoneLogin(res)
        setScreen(Screens.confirmPhoneCode)
      })
      .catch((err) => {
        setScreen(Screens.verifyPhone)

        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setErrorMsg(message)
      })
    setLoad(loaded)
  }

  const sendOTPCode = (email: string) => {
    const loaded = magicLoginService
      .createMagicLogin(email)
      .then((res) => {
        setMagicLogin(res)
        setScreen(Screens.confirmCode)
      })
      .catch((err) => {
        setScreen(Screens.enterEmail)
        setActiveKey(loginWithPhone ? ScreenTabs.phoneNo : ScreenTabs.email)

        const isErrorUnknownEmail = err?.networkError?.errorCode === magicLoginCodes.MagicBadRequest && screen === Screens.enterEmail
        const message = isErrorUnknownEmail
          ? formatMessage({id: "errors.magic.PBMagicUnexpected"})
          : getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setErrorMsg(message)

        tracking.loginOTPError(loginWithPhone ? "otp_phone" : "otp")
        tracking.loginErrorDetails(message)
      })
    setLoad(loaded)
  }

  const handleMultipleLogins = (logins: MagicLogin[]) => {
    setMultipleDomains(logins)
    setScreen(Screens.domainSelector)
  }

  const handleSSOLogin = (tenant: MagicLogin) => {
    setTenantSSO(tenant)
    setScreen(Screens.domainSelector)
  }

  const handleGoBack = () => {
    confirmPhone ? setScreen(Screens.verifyPhone) : setScreen(Screens.enterEmail)
  }

  const handleResendCode = () => {
    confirmPhone ? sendPhoneOTPCode(identifier) : sendOTPCode(identifier)
  }

  const handleSkipPhoneNo = () => {
    localStorage.setItem(VERIFY_PHONE_NO, "{}")
    tracking.phoneCaptureSkip("phone_number_skip", LoginType.phoneCapture)
    History.replace({pathname: "/"})
  }

  const handleOnTabSelect = (tab) => {
    setErrorMsg("")
    setActiveKey(tab)
    setScreen(Screens.enterEmail)
  }

  const handleHideTabs = () => {
    setEnabledSSO(true)
  }

  const getTabs = () => {
    return [
      {
        title: formatMessage({id: "platform.oneTimePassword.tabs.email"}),
        key: ScreenTabs.email,
        component: <EnterEmail load={load} error={errorMsg} onClick={handleLogin} onSSO={handleHideTabs} />
      },
      {
        title: formatMessage({id: "platform.oneTimePassword.tabs.phoneNumber"}),
        key: ScreenTabs.phoneNo,
        component: <EnterPhone load={load} error={errorMsg} onClick={handleLogin} />
      }
    ]
  }

  const resetLocalStorage = () => {
    localStorage.removeItem(VERIFY_PHONE_NO)
    localStorage.removeItem(TREND_RATE_HUBS_DISMISS)
    localStorage.setItem(TREND_RATE_HUB_VISIBLE, localStorage.getItem(TREND_RATE_HUB_VISIBLE)?.split(" ")[0] || "")
  }

  useEffect(() => {
    resetLocalStorage()

    const userAccounts: UserAccountForCookie[] = getUserAccountCookie()
    if (!getTenantFromPath() && userAccounts.length > 0) {
      History.push("welcome/login/password")
    }
    if (location?.state?.noAccessTokenError) {
      setErrorMsg("platform.oneTimePassword.error.noAccessToken")
      History.replace({pathname: location.pathname, state: {}})
    }

    if (window.location.pathname.includes("/activate")) {
      localStorage.setItem("login_type", LoginType.adminActivationInvite)
      localStorage.setItem("login_product", LoginProduct.activation)
    } else {
      localStorage.setItem("login_type", LoginType.otpLogin)
      localStorage.setItem("login_product", LoginProduct.login)
    }
    localStorage.setItem("login_domain", getTenantFromPath() || "")
  }, [])

  return (
    <LoginBackground>
      {screen === Screens.enterEmail && enabledSSO && (
        <EnterEmail load={load} error={errorMsg} onClick={handleLogin} onSSO={handleHideTabs} />
      )}
      {screen === Screens.enterEmail && !enabledSSO && (
        <StyledTabs
          id="otp_enterEmail_enterPassword"
          addMarginTop
          mountOnEnter
          unmountOnExit
          activeKey={activeKey}
          onSelect={handleOnTabSelect}
          tabs={getTabs()}
        />
      )}
      {screen === Screens.confirmCode && (
        <ConfirmCode
          login={magicLogin}
          email={identifier}
          loginWithPhone={loginWithPhone}
          goBack={handleGoBack}
          onResend={handleResendCode}
          onMultipleLogins={handleMultipleLogins}
          onRedirect={handleSSOLogin}
          onTabChange={handleOnTabSelect}
        />
      )}
      {screen === Screens.domainSelector && (
        <DomainSelector logins={multipleDomains} loginWithPhone={loginWithPhone} ssoTenant={tenantSSO} onTabChange={handleOnTabSelect} />
      )}
      {screen === Screens.verifyPhone && (
        <VerifyPhone phoneNo={identifier} load={load} onClick={handleVerifyPhoneNo} error={errorMsg} skipPhoneNo={handleSkipPhoneNo} />
      )}
      {screen === Screens.confirmPhoneCode && (
        <ConfirmPhoneCode
          login={confirmPhoneLogin}
          phone={identifier}
          goBack={handleGoBack}
          onResend={handleResendCode}
          skipPhoneNo={handleSkipPhoneNo}
        />
      )}
      <Flex gap={1} alignItems="center" justifyContent="center" marginTop={screen === Screens.verifyPhone ? 3 : 2}>
        <Body small weight="light">
          <FormattedMessage id="platform.oneTimePassword.footer.text" />
        </Body>
        <Link href={formatMessage({id: "platform.oneTimePassword.footer.link"})} target="_blank">
          <Flex alignItems="center">
            <FormattedMessage id="platform.oneTimePassword.footer.linkText" />
            <Icon icon={IconTypes.chevronRight} size={12} marginLeft={0.5} />
          </Flex>
        </Link>
      </Flex>
    </LoginBackground>
  )
}

export default withRouter(OneTimePassword)
